/**
 * The UI test suite as it actually exists in the project workspace.
 *
 * Why this replaces "Generated Code"
 * ----------------------------------
 * The old tab rendered whatever this session happened to pass to
 * save_test_artifacts. That made sense when the agent regenerated a suite from
 * scratch every run, but it no longer works that way: it reads the existing
 * suite first and then decides KEEP / HEAL / ADD / RETIRE per test. A run that
 * correctly concludes "nothing needs changing" saves nothing, so the tab read
 * "No generated code yet." — while nine perfectly good test files sat in the
 * repo, which is exactly the wrong impression to give.
 *
 * The workspace git repo is the authoritative store for test code (see
 * docs/CANONICAL_STORES.md), so that is what this shows. Files touched by the
 * current session are marked, which turns the tab into the thing you actually
 * want to see: the whole suite, with this run's changes called out.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import apiService from '../services/api.service';
import GeneratedCodeViewer from './GeneratedCodeViewer';

interface Props {
  projectId?: string | number | null;
  /** Target URL of the run — used to scope the listing to this domain. */
  url?: string;
  /** Paths written by the current session, marked in the tree. */
  changedPaths?: string[];
}

// Only fetch things worth showing as source, and put a ceiling on it: the
// contents are fetched eagerly so the tree can be browsed without a round
// trip per click, and an unbounded repo would make that painful.
const TEXT_EXT = /\.(py|ts|tsx|js|jsx|ya?ml|ini|cfg|toml|json|md|txt|sh)$/i;
const MAX_FILES = 200;

const LANG_BY_EXT: Record<string, string> = {
  py: 'python',
  ts: 'typescript',
  js: 'javascript',
  yaml: 'yaml',
  yml: 'yaml',
  ini: 'ini',
  json: 'text',
  md: 'text',
};

const languageOf = (path: string): string =>
  LANG_BY_EXT[path.split('.').pop()?.toLowerCase() || ''] || 'text';

const domainOf = (url?: string): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const CodeRepositoryTab: React.FC<Props> = ({ projectId, url, changedPaths }) => {
  const [files, setFiles] = useState<{ path: string; content: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const domain = useMemo(() => domainOf(url), [url]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { files: paths } = await apiService.listWorkspaceFiles(String(projectId));

        // Show the whole repository, not just this domain's UI suite. The
        // first version filtered to ui_tests/<domain>/, which hid conftest
        // files, helpers, API tests and anything else the project owns — and
        // "repository" that only shows a tenth of the repository is a lie.
        // The tree groups by directory, so the domain's suite still reads as
        // its own section.
        const wanted = paths
          .filter((p) => TEXT_EXT.test(p))
          .slice(0, MAX_FILES);

        // Fetch in parallel — a suite is a handful of files, and a partial
        // failure should cost that one file rather than the whole tab.
        const loaded = await Promise.all(
          wanted.map(async (path) => {
            try {
              const { content } = await apiService.getWorkspaceFileContent(
                String(projectId),
                path
              );
              return { path, content };
            } catch {
              return { path, content: '// Could not be read from the workspace.' };
            }
          })
        );

        if (!cancelled) setFiles(loaded);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load the workspace');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, domain, reloadKey]);

  // Re-read once the session writes something, so the tab reflects the run
  // without the user having to do anything.
  const changedKey = (changedPaths || []).join('|');
  useEffect(() => {
    if (changedKey) setReloadKey((k) => k + 1);
  }, [changedKey]);

  if (!projectId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a project to browse its test repository.
        </Typography>
      </Box>
    );
  }

  if (loading && files.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column',
                 alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
        <CircularProgress size={26} />
        <Typography variant="body2" color="text.secondary">
          Loading the test repository…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="warning"
          action={<Button size="small" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No test files in the workspace yet
          {domain ? ` for ${domain}` : ''}.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          They appear here once the agent writes and commits a suite.
        </Typography>
      </Box>
    );
  }

  const changed = new Set(changedPaths || []);

  return (
    <GeneratedCodeViewer
      code={{
        files: files.map((f) => ({
          path: f.path,
          content: f.content,
          language: languageOf(f.path),
          framework: 'pytest',
        })),
        summary: {
          total_files: files.length,
          total_test_cases: files.reduce(
            (n, f) => n + (f.content.match(/^\s*(?:async )?def\s+test_/gm) || []).length,
            0
          ),
          frameworks_used: ['pytest'],
        },
      }}
      highlightPaths={changed.size ? Array.from(changed) : undefined}
      highlightLabel="updated this run"
      fullHeight
      title="Test Repository"
    />
  );
};

export default CodeRepositoryTab;
